"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslations, useLocale } from "next-intl";
import { CATEGORIES, FONTS, FONT_CATEGORY_LABELS } from "./data";
import type { Palette, FontOption } from "./data";
import { loc, sortPalette } from "./helpers";
import { PaletteCard } from "./PaletteCard";
import { FontCard } from "./FontCard";
import { PreviewSidebar } from "./PreviewSidebar";
import { CatalogContactForm } from "./CatalogContactForm";

const FONT_CATEGORIES = ["sans-serif", "serif", "display", "mono"] as const;

export function CatalogClient() {
  const t = useTranslations("catalog");
  const locale = useLocale();

  const [selectedPalette, setSelectedPalette] = useState<Palette>(
    CATEGORIES[0].palettes[0],
  );
  const [customColors, setCustomColors] = useState<string[]>(
    sortPalette(CATEGORIES[0].palettes[0].colors),
  );
  const [selectedFont, setSelectedFont] = useState<FontOption>(FONTS[0]);
  const [activeCategoryId, setActiveCategoryId] = useState<string>(
    CATEGORIES[0].id,
  );
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");
  const loadedFontsRef = useRef<Set<string>>(new Set());

  const loadFont = useCallback((font: FontOption) => {
    if (loadedFontsRef.current.has(font.name)) return;
    loadedFontsRef.current.add(font.name);
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${font.googleKey}&display=swap`;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    loadFont(FONTS[0]);
  }, [loadFont]);

  const handleFontSelect = (font: FontOption) => {
    loadFont(font);
    setSelectedFont(font);
  };

  const handlePaletteSelect = (palette: Palette) => {
    setSelectedPalette(palette);
    setCustomColors(sortPalette(palette.colors));
  };

  const handleColorChange = (idx: number, hex: string) => {
    setCustomColors((prev) => {
      const next = [...prev];
      next[idx] = hex;
      return next;
    });
  };

  const handleReverse = () => {
    setCustomColors((prev) => [...prev].reverse());
  };

  const activeCategory =
    CATEGORIES.find((c) => c.id === activeCategoryId) ?? CATEGORIES[0];

  return (
    <div className="min-h-screen pb-20 pt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* page header */}
        <div className="mb-10 text-center">
          <p
            className="mb-2 text-xs font-semibold uppercase tracking-[0.2em]"
            style={{ color: "#3B7EFF" }}
          >
            {t("eyebrow")}
          </p>
          <h1
            className="text-4xl font-bold tracking-tight md:text-5xl"
            style={{ color: "#F5F5F7" }}
          >
            {t("title")}
          </h1>
          <p className="mt-3 text-base" style={{ color: "#8B8B94" }}>
            {t("subtitle")}
          </p>
        </div>

        {/* ── mobile-only preview (top of page, visible only on small screens) ── */}
        <div className="mb-6 lg:hidden">
          <PreviewSidebar
            locale={locale}
            viewMode={viewMode}
            setViewMode={setViewMode}
            customColors={customColors}
            selectedFont={selectedFont}
            selectedPalette={selectedPalette}
            handleColorChange={handleColorChange}
            handleReverse={handleReverse}
            setCustomColors={setCustomColors}
          />
        </div>

        {/* ── main layout: flex row on desktop ────────────────────────────── */}
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
          {/* ── LEFT scrollable: palettes + fonts ────────────────────────── */}
          <div className="min-w-0 flex-1">
            {/* palettes */}
            <section className="mb-10">
              <h2
                className="mb-4 text-xl font-bold tracking-tight"
                style={{ color: "#F5F5F7" }}
              >
                {t("colorPalettes")}
              </h2>
              <div className="mb-5 flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategoryId(cat.id)}
                    className="rounded-full px-6 py-2 text-xs font-semibold transition-all duration-150"
                    style={{
                      background:
                        activeCategoryId === cat.id ? "#1E3FE8" : "#fff",
                      color: activeCategoryId === cat.id ? "#fff" : "#000",
                      border:
                        activeCategoryId === cat.id
                          ? "1px solid transparent"
                          : "1px solid rgba(245,245,247,0.08)",
                    }}
                  >
                    {loc(cat.label, locale)}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {activeCategory.palettes.map((palette) => (
                  <PaletteCard
                    key={palette.id}
                    palette={palette}
                    isSelected={selectedPalette.id === palette.id}
                    onSelect={handlePaletteSelect}
                    locale={locale}
                    activeBadge={t("active")}
                  />
                ))}
              </div>
            </section>

            {/* fonts */}
            <section>
              <h2
                className="mb-4 text-xl font-bold tracking-tight"
                style={{ color: "#F5F5F7" }}
              >
                {t("fontFamilies")}
              </h2>
              <div className="space-y-6">
                {FONT_CATEGORIES.map((cat) => {
                  const catFonts = FONTS.filter((f) => f.category === cat);
                  return (
                    <div key={cat}>
                      <p
                        className="mb-3 text-xs font-semibold uppercase tracking-[0.15em]"
                        style={{ color: "#8B8B94" }}
                      >
                        {loc(FONT_CATEGORY_LABELS[cat], locale)}
                      </p>
                      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                        {catFonts.map((font) => (
                          <FontCard
                            key={font.name}
                            font={font}
                            isSelected={selectedFont.name === font.name}
                            onSelect={handleFontSelect}
                            isLoaded={loadedFontsRef.current.has(font.name)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          {/* ── RIGHT sticky sidebar (desktop only) ───────────────────── */}
          <div
            className="hidden shrink-0 lg:block"
            style={{
              width: "340px",
              position: "sticky",
              top: "88px",
              alignSelf: "flex-start",
            }}
          >
            <PreviewSidebar
              locale={locale}
              viewMode={viewMode}
              setViewMode={setViewMode}
              customColors={customColors}
              selectedFont={selectedFont}
              selectedPalette={selectedPalette}
              handleColorChange={handleColorChange}
              handleReverse={handleReverse}
              setCustomColors={setCustomColors}
            />
          </div>
        </div>

        {/* ── catalog inquiry form ─────────────────────────────────── */}
        <CatalogContactForm
          customColors={customColors}
          selectedPalette={selectedPalette}
          selectedFont={selectedFont}
          locale={locale}
        />
      </div>
    </div>
  );
}
